import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Chip,
  TextField,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDashboardFooter } from '../../contexts/DashboardFooterContext';
import { BUDGET_RANGES } from '../homeowner-preferences-quiz/constants';
import './styles.scss';

// Types based on backend GraphQL schema
export interface ProjectQuizResult {
  projectType: string;
  designItems: string[];
  specialNeeds: string[];
  conceptPresentation: string[]; // Changed to array to support multiple selections
  budgetRange?: string;
  projectAddress?: {
    id: string;
    streetAddress1: string;
    streetAddress2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  otherValues?: {
    propertyType: string;
    designItems: string;
    specialNeeds: string;
  };
}

export interface ProjectQuizProps {
  onComplete?: (result: ProjectQuizResult) => void;
  onBack?: () => void;
  initialData?: {
    projectType?: string;
    designItems?: string[];
    specialNeeds?: string[];
    conceptPresentation?: string | string[]; // Support both single value (from backend) and array (for multiple selections)
    projectAddress?: {
      id?: string;
      streetAddress1?: string;
      streetAddress2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
    budgetRange?: string;
  } | null;
  /**
   * Optional initial step override (1-6). Useful when returning to an in-progress quiz.
   */
  initialStep?: number;
}

// Property Types
const propertyTypes = [
  { value: 'PrimaryResidence', label: 'Primary Residence' },
  { value: 'VacationHomeLakeHouse', label: 'Vacation Home: Lake House Design' },
  { value: 'VacationHomeBeachHouse', label: 'Vacation Home: Coastal Living Design/Beach House' },
  { value: 'VacationHomeLodge', label: 'Vacation Home: Mountain/Lodge Design' },
  { value: 'RentalProperty', label: 'Rental Property/AirBnB Design' },
  { value: 'SmallCommercial', label: 'Small Commercial Projects' },
  { value: 'Other', label: 'Other' }
];

// Design Items
const designItems = [
  { value: 'FULL_HOME', label: 'Full Home Design' },
  { value: 'LIVING_ROOM', label: 'Living Room Design' },
  { value: 'DINING_ROOM', label: 'Dining Room Design' },
  { value: 'KITCHEN', label: 'Kitchen Design' },
  { value: 'PRIMARY_BATHROOM', label: 'Primary Bathroom Design' },
  { value: 'OTHER_BATHROOM', label: 'Other Bathroom Design' },
  { value: 'PRIMARY_BEDROOM', label: 'Primary Bedroom Design' },
  { value: 'GUEST_BEDROOM', label: 'Guest Bedroom Design' },
  { value: 'KIDS_BEDROOM', label: 'Kids Bedroom' },
  { value: 'BASEMENT', label: 'Basement Design' },
  { value: 'MUDROOM', label: 'Mudroom Design' },
  { value: 'ENTRYDESIGN', label: 'Entry Design' },
  { value: 'OUTDOOR_SPACE', label: 'Outdoor Space Design' },
  { value: 'SPACE_PLANNING', label: 'Space Planning' },
  { value: 'COLOR_CONSULTING', label: 'Color Consulting' },
  { value: 'LIGHTING', label: 'Lighting Design' },
  { value: 'FURNITURE_SELECTION', label: 'Furniture Selection' },
  { value: 'FAMILY_ROOM', label: 'Living Room Design' },
  { value: 'EVENT_DECOR', label: 'Event/Holiday Decor' },
  { value: 'ART_SELECTION', label: 'Art Selection' },
  { value: 'ANTIQUE_SELECTION', label: 'Antique Selection' },
  { value: 'HOME_STAGING', label: 'Home Staging' },
  { value: 'HOME_OFFICE', label: 'Home Office Design' },
  { value: 'OTHER', label: 'Other' }
];

// Special Needs
const specialNeeds = [
  { value: 'ACCESSIBILITY_FEATURES_FOR_DISABILITIES', label: 'Accessibility Features For Disabilities' },
  { value: 'PET_FRIENDLY_ELEMENTS', label: 'Pet-Friendly Design Elements' },
  { value: 'SMART_HOME_INTEGRATION', label: 'Smart Home Integration' },
  { value: 'CUSTOM_STORAGE_SOLUTIONS', label: 'Custom Storage Solutions' },
  { value: 'SPECIFIC_ACOUSTIC_TREATMENTS', label: 'Specific Acoustic Treatments' },
  { value: 'ENVIRONMENTALLY_SUSTAINABLE_MATERIALS', label: 'Environmentally Sustainable Materials' },
  { value: 'UNIQUE_LIGHTING_EFFECTS', label: 'Unique Lighting Effects' },
  { value: 'HISTORICAL_PRESERCATION_CONSIDERATION', label: 'Historical Preservation Consideration' },
  { value: 'OUTDOOR_LIVING_CONNECTIONS', label: 'Outdoor Living Connections' },
  { value: 'SPECIALIZED_VENTILATION_SYSTEMS', label: 'Specialized Ventilation Systems' },
  { value: 'FENG_SHUI', label: 'Feng Shui' },
  { value: 'ART_INTEGRATION', label: 'Art Integration' },
  { value: 'WINE_CELLAR', label: 'Wine Cellar' },
  { value: 'HOME_GYM', label: 'Home Gym' },
  { value: 'OTHER', label: 'Other' }
];

// Presentation Types
const presentationTypes = [
  { value: 'boards', label: 'Mood & Vision Boards', description: 'Traditional mood boards and material boards' },
  { value: 'twoD', label: '2D Layouts', description: 'Flat drawings and floor plans' },
  { value: 'threeD', label: '3D Layouts', description: 'Three-dimensional visualizations' },
  { value: 'virtual', label: 'Virtual Walk Through', description: 'Immersive VR experiences' },
  { value: 'no_preference', label: 'No Preference', description: 'No specific preference' }
];

export const ProjectQuiz: React.FC<ProjectQuizProps> = ({ onComplete, onBack, initialData, initialStep }) => {
  const totalSteps = 6;

  const [currentStep, setCurrentStep] = useState(() => {
    // If an initialStep is provided (e.g., returning to this quiz), honor it
    if (initialStep && initialStep >= 1 && initialStep <= totalSteps) {
      return initialStep;
    }
    return 1;
  });
  // REMOVED the useEffect that syncs initialStep -> currentStep
  // This was causing issues when initialStep changed due to parent re-renders
  // The useState initializer above handles the initial value correctly
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>(() => {
    const value = initialData?.projectType || '';
    return value;
  });
  const [selectedDesignItems, setSelectedDesignItems] = useState<string[]>(() => {
    const value = initialData?.designItems || [];
    return value;
  });
  const [selectedSpecialNeeds, setSelectedSpecialNeeds] = useState<string[]>(() => {
    const value = initialData?.specialNeeds || [];
    return value;
  });
  const [selectedPresentationTypes, setSelectedPresentationTypes] = useState<string[]>(() => {
    // Handle both single value (from GraphQL) and array (if already converted)
    const value = initialData?.conceptPresentation 
      ? (Array.isArray(initialData.conceptPresentation) 
          ? initialData.conceptPresentation 
          : [initialData.conceptPresentation])
      : [];
    return value;
  });
  const [budgetRange, setBudgetRange] = useState<string>(() => {
    return initialData?.budgetRange || BUDGET_RANGES[0]?.value || '';
  });
  const [otherPropertyType, setOtherPropertyType] = useState('');
  const [otherDesignItems, setOtherDesignItems] = useState('');
  const [otherSpecialNeeds, setOtherSpecialNeeds] = useState('');

  const [address, setAddress] = useState(() => {
    const addr = {
      streetAddress1: initialData?.projectAddress?.streetAddress1 || '',
      streetAddress2: initialData?.projectAddress?.streetAddress2 || '',
      city: initialData?.projectAddress?.city || '',
      state: initialData?.projectAddress?.state || '',
      zipCode: initialData?.projectAddress?.zipCode || ''
    };
    return addr;
  });

  const { setFooter, clearFooter } = useDashboardFooter();
  const hasInitializedRef = React.useRef(false);
  // Track if user has started interacting with the form
  const hasUserInteractedRef = React.useRef(false);
  
  // CRITICAL: Use refs to ensure footer callbacks always have access to latest state
  // This prevents stale closure issues when the footer context storesButton elements
  const handleNextRef = React.useRef<() => void>(() => {});
  const handleBackRef = React.useRef<() => void>(() => {});

  // Sync state when initialData is provided (for retake flow)
  // This ensures that if initialData arrives after component mount, we still populate the state
  // IMPORTANT: Only sync on initial mount, NOT when initialData changes after user interaction
  useEffect(() => {

    // CRITICAL: Don't overwrite user selections if they've started interacting
    if (hasUserInteractedRef.current) {
      console.log('ProjectQuiz: User has interacted, skipping initialData sync to preserve selections');
      return;
    }

    if (initialData) {
      
      // Always update from initialData if it's provided (for retake flow)
      if ('projectType' in initialData && initialData.projectType !== undefined) {
        setSelectedPropertyType(initialData.projectType);
      } else {
      }
      
      if ('designItems' in initialData && initialData.designItems !== undefined) {
        setSelectedDesignItems(initialData.designItems);
      } else {
      }
      
      if ('specialNeeds' in initialData && initialData.specialNeeds !== undefined) {
        setSelectedSpecialNeeds(initialData.specialNeeds);
      } else {
      }
      
      // Handle conceptPresentation - check if key exists, not just truthiness
      // Support both single value (from GraphQL) and array (if already converted)
      if ('conceptPresentation' in initialData) {
        if (initialData.conceptPresentation) {
          const value = Array.isArray(initialData.conceptPresentation)
            ? initialData.conceptPresentation
            : [initialData.conceptPresentation];
          setSelectedPresentationTypes(value);
        } else {
          setSelectedPresentationTypes([]);
        }
      } else {
      }
      
      if ('projectAddress' in initialData && initialData.projectAddress) {
        const newAddress = {
          streetAddress1: initialData.projectAddress.streetAddress1 || '',
          streetAddress2: initialData.projectAddress.streetAddress2 || '',
          city: initialData.projectAddress.city || '',
          state: initialData.projectAddress.state || '',
          zipCode: initialData.projectAddress.zipCode || ''
        };
        setAddress(newAddress);
      } else {
      }
      
      if ('budgetRange' in initialData) {
        setBudgetRange(initialData.budgetRange || BUDGET_RANGES[0]?.value || '');
      }
      hasInitializedRef.current = true;
    } else {
    }
  }, [initialData]);

  // ✅ No useTheme needed
  const isMobile = useMediaQuery('(max-width:600px)');

  // Heuristic: long labels become full-width on mobile
  const isLong = (label: string) => isMobile && label.length > 26;

  const handlePropertyTypeSelect = (value: string) => {
    hasUserInteractedRef.current = true;
    setSelectedPropertyType(value);
  };

  const toggleDesignItem = (value: string) => {
    hasUserInteractedRef.current = true;
    setSelectedDesignItems(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  const toggleSpecialNeed = (value: string) => {
    hasUserInteractedRef.current = true;
    setSelectedSpecialNeeds(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  const togglePresentationType = (value: string) => {
    hasUserInteractedRef.current = true;
    console.log('🎯 togglePresentationType called with:', {
      value,
      currentSelection: selectedPresentationTypes,
      willToggle: selectedPresentationTypes.includes(value) ? 'remove' : 'add',
      newSelection: selectedPresentationTypes.includes(value) 
        ? selectedPresentationTypes.filter(v => v !== value)
        : [...selectedPresentationTypes, value]
    });
    setSelectedPresentationTypes(prev => {
      const newSelection = prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
      console.log('🎯 togglePresentationType - State updated:', {
        old: prev,
        new: newSelection,
        length: newSelection.length,
        items: newSelection.map((p: string, i: number) => `${i + 1}. ${p}`).join(', ')
      });
      return newSelection;
    });
  };

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      return;
    }

    // Log the selected presentation types before creating result
    console.log('🎯 ProjectQuiz handleNext - Selected presentation types:', {
      selectedPresentationTypes,
      isArray: Array.isArray(selectedPresentationTypes),
      length: selectedPresentationTypes.length,
      values: selectedPresentationTypes,
      stringified: JSON.stringify(selectedPresentationTypes),
      allItems: selectedPresentationTypes.map((p: string, i: number) => `${i + 1}. ${p}`).join(', ')
    });

    // Filter out falsy values and ensure we have valid enum values
    const validPresentationTypes = ['boards', 'twoD', 'threeD', 'virtual'];
    const conceptPresentationArray = selectedPresentationTypes
      .filter(Boolean)
      .filter((val: string) => validPresentationTypes.includes(val)); // Only include valid enum values
    
    console.log('🎯 ProjectQuiz handleNext - Filtered conceptPresentation array:', {
      raw: selectedPresentationTypes,
      filtered: conceptPresentationArray,
      length: conceptPresentationArray.length,
      stringified: JSON.stringify(conceptPresentationArray),
      allItems: conceptPresentationArray.map((p: string, i: number) => `${i + 1}. ${p}`).join(', '),
      validEnumValues: validPresentationTypes,
      // Check if any invalid values were filtered out
      invalidFiltered: selectedPresentationTypes.filter((val: string) => val && !validPresentationTypes.includes(val))
    });
    
    // Warn if invalid values were filtered out
    const invalidValues = selectedPresentationTypes.filter((val: string) => val && !validPresentationTypes.includes(val));
    if (invalidValues.length > 0) {
      console.warn('⚠️ Invalid presentation types filtered out:', {
        invalid: invalidValues,
        valid: validPresentationTypes
      });
    }

    const result: ProjectQuizResult = {
      projectType: selectedPropertyType === 'Other' ? 'Other' : selectedPropertyType,
      designItems: selectedDesignItems.map(i => (i === 'OTHER' ? 'OTHER' : i)).filter(Boolean),
      specialNeeds: selectedSpecialNeeds.map(i => (i === 'OTHER' ? 'OTHER' : i)).filter(Boolean),
      conceptPresentation: conceptPresentationArray, // Return array of all selected types
      budgetRange,
      projectAddress: {
        id: `addr_${Date.now()}`,
        streetAddress1: address.streetAddress1 || '',
        streetAddress2: address.streetAddress2 || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zipCode || ''
      },
      otherValues: {
        propertyType: selectedPropertyType === 'Other' ? otherPropertyType : '',
        designItems: selectedDesignItems.includes('OTHER') ? otherDesignItems : '',
        specialNeeds: selectedSpecialNeeds.includes('OTHER') ? otherSpecialNeeds : ''
      }
    };

    console.log('🎯 ProjectQuiz handleNext - Final result object:', {
      result,
      conceptPresentation: result.conceptPresentation,
      conceptPresentationType: typeof result.conceptPresentation,
      conceptPresentationIsArray: Array.isArray(result.conceptPresentation),
      conceptPresentationLength: Array.isArray(result.conceptPresentation) ? result.conceptPresentation.length : 0,
      conceptPresentationStringified: JSON.stringify(result.conceptPresentation)
    });

    onComplete?.(result);
  }, [
    currentStep,
    totalSteps,
    selectedPropertyType,
    selectedDesignItems,
    selectedSpecialNeeds,
    selectedPresentationTypes,
    otherPropertyType,
    otherDesignItems,
    otherSpecialNeeds,
    budgetRange,
    address,
    onComplete
  ]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
    else onBack?.();
  }, [currentStep, onBack]);
  
  // CRITICAL: Keep refs updated with latest callbacks
  // This ensures the footer buttons always call the latest version
  useEffect(() => {
    handleNextRef.current = handleNext;
    handleBackRef.current = handleBack;
  }, [handleNext, handleBack]);

  const canContinue = () => {
    switch (currentStep) {
      case 1:
        return selectedPropertyType !== '' && (selectedPropertyType !== 'Other' || otherPropertyType.trim() !== '');
      case 2:
        return true;
      case 3:
        return selectedDesignItems.length > 0 && (!selectedDesignItems.includes('OTHER') || otherDesignItems.trim() !== '');
      case 4:
        return !selectedSpecialNeeds.includes('OTHER') || otherSpecialNeeds.trim() !== '';
      case 5:
        const canContinueStep5 = selectedPresentationTypes.length > 0;
        console.log('🔍 canContinue check for step 5:', {
          selectedPresentationTypes,
          length: selectedPresentationTypes.length,
          canContinue: canContinueStep5,
          items: selectedPresentationTypes.map((p: string, i: number) => `${i + 1}. ${p}`).join(', ')
        });
        return canContinueStep5;
      case 6:
        return budgetRange !== '';
      default:
        return false;
    }
  };

  // CRITICAL: Use wrapper functions that call refs to ensure latest callback is always used
  // This prevents stale closure issues when footer context stores button elements
  const onBackClick = React.useCallback(() => {
    console.log('🔙 ProjectQuiz: Back button clicked, calling handleBackRef.current');
    handleBackRef.current();
  }, []);
  
  const onNextClick = React.useCallback(() => {
    console.log('➡️ ProjectQuiz: Next/Save button clicked, calling handleNextRef.current');
    handleNextRef.current();
  }, []);

  React.useEffect(() => {
    const isDisabled = !canContinue();
    console.log('🔄 ProjectQuiz: Setting footer, canContinue:', !isDisabled, 'currentStep:', currentStep);
    
    setFooter(
      <>
        <Button 
          onClick={onBackClick} 
          variant="text" 
          startIcon={<ChevronLeft sx={{ width: 16, height: 16 }} />}
          sx={{ fontSize: { xs: '14px', sm: '16px' } }}
        >
          Back
        </Button>
        <Button
          onClick={onNextClick}
          variant="contained"
          endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />}
          sx={{ 
            px: 4,
            fontSize: { xs: '14px', sm: '16px' }
          }}
          disabled={isDisabled}
        >
          {currentStep === totalSteps ? `Save and Continue` : `Continue ${currentStep} of ${totalSteps}`}
        </Button>
      </>
    );
    return () => clearFooter();
  }, [currentStep, setFooter, clearFooter, onBackClick, onNextClick, canContinue]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Container maxWidth="md" sx={{ pl: 2, pr: 2 }}>
            <Box textAlign="center" mb={4}>
              <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
                What is the property type for your project?
              </Typography>
            </Box>

            <Box maxWidth="800px" mx="auto">
              <Box className="tag-grid" display="flex" flexWrap="wrap" gap={1} justifyContent="start">
                {propertyTypes.map((type) => (
                  <Chip
                    key={type.value}
                    className="tag-chip"
                    data-span={isLong(type.label) ? 'full' : undefined}
                    label={type.label}
                    onClick={() => handlePropertyTypeSelect(type.value)}
                    variant={selectedPropertyType === type.value ? 'filled' : 'outlined'}
                    color='secondary'
                    sx={{
                      fontSize: '1rem',
                      py: 0.5, // 8px
                      px: 0.5, // 8px
                      height: 'auto',
                      minWidth: 'auto',
                      width: 'fit-content',
                      borderRadius: 50,
                      borderWidth: 2
                    }}
                  />
                ))}
              </Box>

              {selectedPropertyType === 'Other' && (
                <Box mt={4} display="flex" justifyContent="center">
                  <Box width="100%" maxWidth="500px">
                    <Typography variant="body2" mb={1} sx={{ color: 'text.secondary' }}>
                      Please specify
                    </Typography>
                    <Typography variant="body2" mb={1} sx={{ color: 'text.secondary' }}>
                      Other
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="another clever design experience"
                      value={otherPropertyType}
                      onChange={(e) => setOtherPropertyType(e.target.value)}
                      variant="standard"
                      sx={{
                        '& .MuiInput-root:before': { borderBottom: `1px solid`, borderBottomColor: 'secondary.main' },
                        '& .MuiInput-root:hover:before': { borderBottomColor: 'secondary.main' },
                        '& .MuiInput-root:after': { borderBottomColor: 'secondary.main' },
                        '& .MuiInputBase-root': { borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderRadius: 0 }
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Container>
        );

      case 2:
        return (
          <Container maxWidth="md" sx={{ pl: 2, pr: 2 }}>
            <Box textAlign="center" mb={4}>
              <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
                Where is this project located?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please enter the ZIP code so we can match you with designers in your area.
              </Typography>
            </Box>

            <Box maxWidth="400px" mx="auto">
              <TextField
                fullWidth
                placeholder="Zipcode"
                value={address.zipCode}
                onChange={(e) => setAddress({ ...address, zipCode: e.target.value })}
                variant="outlined"
                sx={{ '& .MuiInputBase-root': { borderRadius: 4 } }}
              />
            </Box>
          </Container>
        );

      case 3:
        return (
          <Container maxWidth="md" sx={{ pl: 2, pr: 2 }}>
            <Box textAlign="center" mb={4}>
              <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
                Which of the following best describes your project?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (Select All That Apply)
              </Typography>
            </Box>

            <Box className="tag-grid" display="flex" flexWrap="wrap" gap={1} justifyContent="start">
              {designItems.map((item) => (
                <Chip
                  key={item.value}
                  className="tag-chip"
                  data-span={isLong(item.label) ? 'full' : undefined}
                  label={item.label}
                  onClick={() => toggleDesignItem(item.value)}
                  variant={selectedDesignItems.includes(item.value) ? 'filled' : 'outlined'}
                  color='secondary'
                  sx={{
                    fontSize: '0.9rem',
                    py: 0.5, // 8px
                    px: 0.5, // 8px
                    height: 'auto',
                    minWidth: 'auto',
                    width: 'fit-content',
                    borderRadius: 50,
                  }}
                />
              ))}
            </Box>

            {selectedDesignItems.includes('OTHER') && (
              <Box mt={4} display="flex" justifyContent="center">
                <Box width="100%" maxWidth="500px">
                  <Typography variant="body2" mb={1} sx={{ color: 'text.secondary' }}>
                    Please specify
                  </Typography>
                  <Typography variant="body2" mb={1} sx={{ color: 'text.secondary' }}>
                    Other
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="another clever design experience"
                    value={otherDesignItems}
                    onChange={(e) => setOtherDesignItems(e.target.value)}
                    variant="standard"
                    sx={{
                      '& .MuiInput-root:before': { borderBottom: `1px solid`, borderBottomColor: 'secondary.main' },
                      '& .MuiInput-root:hover:before': { borderBottomColor: 'secondary.main' },
                      '& .MuiInput-root:after': { borderBottomColor: 'secondary.main' },
                      '& .MuiInputBase-root': { borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderRadius: 0 }
                    }}
                  />
                </Box>
              </Box>
            )}
          </Container>
        );

      case 4:
        return (
          <Container maxWidth="md" sx={{ pl: 2, pr: 2 }}>
            <Box textAlign="center" mb={4}>
              <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
                Any special needs or considerations for this project?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (Select All That Apply)
              </Typography>
            </Box>

            <Box className="tag-grid" display="flex" flexWrap="wrap" gap={1} justifyContent="start">
              {specialNeeds.map((need) => (
                <Chip
                  key={need.value}
                  className="tag-chip"
                  data-span={isLong(need.label) ? 'full' : undefined}
                  label={need.label}
                  onClick={() => toggleSpecialNeed(need.value)}
                  variant={selectedSpecialNeeds.includes(need.value) ? 'filled' : 'outlined'}
                  color='secondary'
                  sx={{
                    fontSize: '0.9rem',
                    py: 0.5, // 8px
                    px: 0.5, // 8px
                    height: 'auto',
                    minWidth: 'auto',
                    width: 'fit-content',
                    borderRadius: 50,
                    borderWidth: 2,
                    border: '1px solid #D22268 !important'
                  }}
                />
              ))}
            </Box>

            {selectedSpecialNeeds.includes('OTHER') && (
              <Box mt={4} display="flex" justifyContent="center">
                <Box width="100%" maxWidth="500px">
                  <Typography variant="body2" mb={1} sx={{ color: 'text.secondary' }}>
                    Please specify
                  </Typography>
                  <Typography variant="body2" mb={1} sx={{ color: 'text.secondary' }}>
                    Other
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="another clever design experience"
                    value={otherSpecialNeeds}
                    onChange={(e) => setOtherSpecialNeeds(e.target.value)}
                    variant="standard"
                    sx={{
                      '& .MuiInput-root:before': { borderBottom: `1px solid`, borderBottomColor: 'secondary.main' },
                      '& .MuiInput-root:hover:before': { borderBottomColor: 'secondary.main' },
                      '& .MuiInput-root:after': { borderBottomColor: 'secondary.main' },
                      '& .MuiInputBase-root': { borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderRadius: 0 }
                    }}
                  />
                </Box>
              </Box>
            )}
          </Container>
        );

      case 5:
        return (
          <Container maxWidth="md" sx={{ pl: 2, pr: 2 }}>
            <Box textAlign="center" mb={4}>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
                How would you like your design concepts presented?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                (Select All That Apply)
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {presentationTypes.map((type) => (
                <Grid item xs={12} sm={6} md={4} key={type.value}>
                  <Card
                      onClick={() => togglePresentationType(type.value)}
                    variant={selectedPresentationTypes.includes(type.value) ? 'elevation' : 'outlined'}
                      sx={{
                      cursor: 'pointer',
                      height: '100%',
                      minHeight: '180px',
                      boxShadow: 'none',
                      border: selectedPresentationTypes.includes(type.value) 
                        ? '2px solid' 
                        : '1px solid',
                      borderColor: selectedPresentationTypes.includes(type.value) 
                        ? 'secondary.main' 
                        : 'divider',
                      backgroundColor: selectedPresentationTypes.includes(type.value)
                        ? 'secondary.main'
                        : 'transparent',
                      '&:hover': {
                          borderColor: 'secondary.main',
                        },
                      transition: 'all 0.2s ease-in-out'
                      }}
                    >
                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                      <Typography 
                        variant="h6" 
                      sx={{
                          mb: 1.5,
                          fontWeight: 'bold',
                          color: selectedPresentationTypes.includes(type.value) ? '#FFFFFF' : 'secondary.main',
                          textAlign: 'left'
                      }}
                    >
                      {type.label}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: selectedPresentationTypes.includes(type.value) ? '#FFFFFF' : 'text.secondary',
                          textAlign: 'left',
                          fontWeight: 'normal'
                        }}
                      >
                        {type.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        );

      case 6:
        return (
          <Container maxWidth="md" sx={{ pl: 2, pr: 2 }}>
            <Box textAlign="center" mb={4}>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
                Tell us about your budget range.
              </Typography>
            </Box>

            <Box maxWidth={520} mx="auto">
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
                Select a budget range
              </Typography>
              <TextField
                select
                fullWidth
                value={budgetRange}
                onChange={(e) => {
                  hasUserInteractedRef.current = true;
                  setBudgetRange(e.target.value);
                }}
                SelectProps={{ native: true }}
                sx={{ "& .MuiInputBase-root": { borderRadius: 3 } }}
              >
                {BUDGET_RANGES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </TextField>
            </Box>
          </Container>
        );

      default:
        return null;
    }
  };

  return <Box pt={0} pb={4}>{renderStep()}</Box>;
};
